import React, {
  useRef, useState, useMemo, useEffect, memo,
} from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Icon } from 'components/atoms';
import { themeType, modifiersType } from 'types';
import { rem } from 'polished';
import { applyStyleModifiers } from 'styled-components-modifiers';

const SEARCH_MODIFIERS = {
  fixSpacing: () => `
    padding: 0;
  `,
};


const SearchInput = memo(({
  value,
  onChange,
  placeholder,
  theme,
  onBlur,
  onFocus,
  stretch,
  showSearch,
  onClick,
  modifiers,
}) => {
  const inputRef = useRef(null);
  const [isFocus, setFocus] = useState(stretch);

  const computedColor = useMemo(
    () => (isFocus ? theme.colors.primary : theme.colors.border),
    [theme.colors.primary, theme.colors.border, isFocus]
  );

  const showInput = useMemo(() => showSearch || isFocus, [isFocus, showSearch]);

  useEffect(() => {
    if (showInput) {
      onFocus();
      if (inputRef && inputRef.current) inputRef.current.focus();
    } else {
      onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInput]);

  const handleClick = () => {
    setFocus(true)
    onClick()
  }

  return (
    <SearchInputWrapper
      onClick={handleClick}
      onBlur={() => setFocus(false)}
      modifiers={modifiers}
    >
      {showInput ? (
        <InputWrapper>
          <Top>
            <input
              type="text"
              value={value}
              onChange={e => onChange(e)}
              placeholder={placeholder}
              ref={inputRef}
            />
            <Icon icon="search" color={computedColor} />
          </Top>
          <Seperator />
        </InputWrapper>
      ) : (
        <ButtonWrapper>
          <Icon icon="search" height={24} />
        </ButtonWrapper>
      )}
    </SearchInputWrapper>
  );
});

SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  stretch: PropTypes.bool,
  showSearch: PropTypes.bool,
  onClick: PropTypes.func,
  modifiers: modifiersType,
};

SearchInput.defaultProps = {
  stretch: false,
  showSearch: false,
  onClick: () => null,
  modifiers: [],
};

const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  padding-left: ${props => props.theme.spacing.l};
  padding-right: ${props => props.theme.spacing.l};
  padding-bottom: ${props => props.theme.spacing.xs2};

  input {
    border: none;
    font-weight: ${props => props.theme.weight.light};
    &::placeholder {
      color: ${props => props.theme.colors.baseLighter};
    }
    padding-bottom: ${rem('9px')};
    padding-top: ${props => props.theme.spacing.xs};
    &:focus {
      outline: none;
    }
  }

  &:hover {
    svg {
      stroke: ${props => props.theme.colors.primary} !important;
    }
  }
  ${applyStyleModifiers(SEARCH_MODIFIERS)};
`;

const Top = styled.div`
  display: flex;
  align-items: center;
`;

const Seperator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${props => props.theme.colors.border};
`;

const ButtonWrapper = styled.button`
  display: flex;
  width: ${props => props.theme.sizes.xl5};
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.xs2};
  background-color: transparent;
  border: none;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`;

const InputWrapper = styled.div`
  min-width: 190px;
`;

SearchInput.propTypes = {
  theme: themeType.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

SearchInput.defaultProps = {
  onBlur: () => null,
  onFocus: () => null,
};

export default withTheme(SearchInput);
